import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import {
  listBrandingTemplates,
  BrandingTemplateOption,
} from '../../utils/contractsApi';

/**
 * Export-summary branding picker (modal body only).
 *
 * Rendered as the `content` of a global `confirmation` modal - the modal owns
 * the Cancel / Export buttons, so this component is just the branding selector.
 * It fetches the firm's branding templates, shows them in our own Dropdown plus
 * a "No branding" option, and reports the current choice up via `onSelect` so
 * the modal's onConfirm can resolve it to the elements and print.
 */
const NO_BRANDING = '__none__';

export const ExportSummaryForm: React.FC<{
  /** Fires on mount (with the default) and on every change: null = no branding. */
  onSelect: (templateId: string | null) => void;
}> = ({ onSelect }) => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<BrandingTemplateOption[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      const list = await listBrandingTemplates();
      if (!alive) return;
      setTemplates(list);
      const initial = list.length ? list[0].id : NO_BRANDING;
      setSelected(initial);
      onSelect(initial === NO_BRANDING ? null : initial);
      setLoading(false);
    })();
    return () => { alive = false; };
    // onSelect is stable from the caller; intentionally run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = [
    ...templates.map((tpl) => ({ value: tpl.id, label: tpl.name })),
    { value: NO_BRANDING, label: t('No branding (plain)') },
  ];

  const handleChange = (val: string | string[]) => {
    const v = Array.isArray(val) ? val[0] : val;
    setSelected(v);
    onSelect(v === NO_BRANDING ? null : v);
  };

  return (
    <div className="space-y-3 min-w-[320px]">
      <p className="text-[12px] text-fg-muted">
        {t('Choose a branding template to apply to the exported summary, or export without branding.')}
      </p>

      <Dropdown
        label={t('Branding')}
        placeholder={loading ? t('Loading templates…') : t('Select branding')}
        options={options}
        value={selected}
        onChange={handleChange}
      />

      {!loading && templates.length === 0 && (
        <p className="text-[11px] text-fg-muted">
          {t('No branding templates found. The summary will export with plain styling.')}
        </p>
      )}
    </div>
  );
};

export default ExportSummaryForm;
